/* eslint-disable import/order */
import "@/@iconify/icons-bundle";
import App from "@/App.vue";
import layoutsPlugin from "@/plugins/layouts";
import { createVuetifyWithTheme } from "@/plugins/vuetify";
import { loadFonts } from "@/plugins/webfontloader";
import router from "@/router";
import { loadTrackingScripts } from "@/utils/Tracking";
import { useUserStore } from "@/utils/UserStore";
import "@core/scss/template/index.scss";
import * as Sentry from "@sentry/vue";
import "@styles/styles.scss";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { createApp } from "vue";
import Vue3Toastify, { type ToastContainerOptions } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useGlobalSetupStore } from "./utils/GlobalSetupStore";
import { useLocationStore } from "./utils/LocationStore";

// toast config
const toastConfig = {
  autoClose: 1000,
  closeButton: true,
  hideProgressBar: true,
  theme: "auto",
  type: "default",
};

loadFonts();

// Create vue app
const app = createApp(App);
Sentry.init({
  app,
  dsn: "https://f43cd47142232c706a3e2e32be8ffcea@o4508427843600384.ingest.us.sentry.io/4508427843796992",
  integrations: [],
  // Raised by Safari browser extensions, not BayBooker code.
  ignoreErrors: [/Invalid call to runtime\.sendMessage\(\)\. Tab not found\./],
});

const pinia = createPinia();
app.use(pinia);
pinia.use(piniaPluginPersistedstate);

const globalSetupStore = useGlobalSetupStore();
let vuetify;
(async () => {
  await globalSetupStore.getGlobalSetup(); // Ensure store is hydrated

  loadTrackingScripts({
    googleAnalyticsId: globalSetupStore.globalSetup.googleAnalyticsId,
    googleTagManagerId: globalSetupStore.globalSetup.googleTagManagerId,
    metaPixelId: globalSetupStore.globalSetup.metaPixelId,
  });

  const themePrimaryColor = globalSetupStore.globalSetup.themePrimaryColor;
  const themePrimaryColorDark = globalSetupStore.globalSetup.themePrimaryColorDark;

  vuetify = createVuetifyWithTheme(themePrimaryColor, themePrimaryColorDark);

  // Use plugins
  app.use(vuetify);
  app.use(router);
  app.use(layoutsPlugin);
  app.use(Vue3Toastify, toastConfig as ToastContainerOptions);

  const locationStore = useLocationStore();
  const userStore = useUserStore();

  // Mount vue app
  router.isReady().then(() => {
    Promise.all([
      locationStore.refreshLocations(),
      globalSetupStore.getGlobalSetup(),
      userStore.init(),
      userStore.getCurrentLocation(),
    ])
    .then(() => {
      app.mount("#app");
    })
    .catch((err) => {
      console.error("Failed to load locations & global setup:", err);
      app.mount("#app");
    });
  });
})();

